import GS from '../grafsync/grafsync';
import SecondaryButton from './SecondaryButton';
import LiteralConfirmationField from '../core/LiteralConfirmationField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import OtherProjectLink from '../core/OtherProjectLink';
import AssociatedProjectDisplay from '../application-core/AssociatedProjectDisplay';
import SelectProjectDialog from '../core/project/SelectProjectDialog';
import SelectResourceLink from '../application-core/associated-project/SelectResourceLink';
import UseTemplateDialog from '../application-core/associated-project/UseTemplateDialog';
import TypeDisplay from '../vocabulary/taxonomies/TypeDisplay';
import PropertyDisplay from '../vocabulary/taxonomies/PropertyDisplay';
import BasicLanguageFieldDisplay from './BasicLanguageFieldDisplay';
import BasicReadField from './BasicReadField';
import LabelledSwitch from './LabelledSwitch';
import { ProjectConsumer } from './providers/ProjectProvider';
import { globalStyles as styles } from './style/gstheme';
import TitledPanel from './TitledPanel';
import TabbedSubmenu from './TabbedSubmenu';
import { ENTITLEMENTS } from '../application-core/constants';
import { MAX_COMPONENT_DESC_LENGTH, MAX_COMPONENT_TITLE_LENGTH, MAX_DEFINITION_LENGTH } from './utils/constants';
import { checkResourceEntitlements, getGraphFromProject, makeCancelable } from './utils/utils';
import withGS from './withGShoc';
import AnnotationPropertyDisplay from './AnnotationPropertyDisplay';
import IRI from './IRI';
import Warning from './Warning';

const noteFields =
    [{
        title: "Comments",
        message: "Comment",
        property: "comment",
        placeholder: "Enter comment text"
    },
    {
        title: "Editorial Notes",
        message: "Editorial Note",
        property: "editorialNote",
        placeholder: "Enter note text"
    },
    {
        title: "Change Notes",
        message: "Change Note",
        property: "changeNote",
        placeholder: "Enter note text"
    }];

const linkedDataSources = [
    {
        name: "DBpedia"
    },
    {
        name: "Wikidata"
    }
];

class ComponentDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isErrored: false,
            workflowUsable: ["Managed", "Trial", "Premium"].includes(GS.getActiveSubscriptionType())
        };
        this.cancellable = null;
    }

    onUpdate = (property, lang, index) => (value) => {
        const exists = this.props.data[property] && this.props.data[property][lang];
        if (!exists) {
            this.props.onAdd(property, value, lang);
        }
        else this.props.onUpdate(property, value, lang, index);
    };

    onUpdateIRI = (property) => (value) => {
        const exists = property in this.props.data && this.props.data[property] !== undefined;
        if (!exists) {
            this.props.onAdd(property, value);
        }
        else this.props.onUpdate(property, value);
    };

    onUpdatePrependLabel = (value) => {
        const exists = this.props.data["iriPrependLabelText"];
        if (!exists) {
            this.props.onAdd("iriPrependLabelText", value);
        }
        else this.props.onUpdate("iriPrependLabelText", value);
    };

    onAddAnnotation = (annotationIRI) => this.props.onAdd("availableAnnotationProperty", annotationIRI);

    onAddClass = (classIRI) => this.props.onAdd("defaultClass", classIRI);

    onAddProperty = (propertyIRI) => this.props.onAdd("sortProperty", propertyIRI);

    onUseTemplate = (templateIRI) => this.props.onAdd("associatedTemplate", templateIRI);

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    onActualUpdate = (property, lang, index, propertyGraphIRI) => (value) => {
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);
    };

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) =>
        this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    includeExistingResource = (property, propertyGraphIRI) => (newIRI) => {
        // We load the concept we are about to use to make sure rendering works okay
        // But if it's external it won't load and that's okay
        GS.loadData([newIRI], {}, this.props.graph).then(() => {
            GS.addProperty(this.props.id, this.props.graph, property, newIRI, null, null, propertyGraphIRI);
        });
    };

    addProject = (projectIRI) => this.props.onAdd("associatedProject", projectIRI);

    handleSortChange = (numerically) => {
        this.props.data.sortNumerically !== undefined ? this.props.onUpdate('sortNumerically', numerically.toString()) : this.props.onAdd('sortNumerically', true.toString());
    };

    handleXLChange = (useXL) => {
        this.props.data.useXL !== undefined ? this.props.onUpdate('useXL', useXL.toString()) : this.props.onAdd('useXL', true.toString());
    };

    handleNotesChange = (resourcesForNotes) => {
        this.props.data.resourcesForNotes !== undefined ? this.props.onUpdate('resourcesForNotes', resourcesForNotes.toString()) : this.props.onAdd('resourcesForNotes', true.toString());
    };

    handleWorkflowChange = (workflow) => {
        this.props.data.activateWorkflow !== undefined ? this.props.onUpdate('activateWorkflow', workflow.toString()) : this.props.onAdd('activateWorkflow', true.toString());
    };

    handleAnnotateChange = (annotate) => {
        this.props.data.activateAnnotate !== undefined ? this.props.onUpdate('activateAnnotate', annotate.toString()) : this.props.onAdd('activateAnnotate', true.toString());
    };

    handleModeChange = (event) => {
        const mode = event.target.value;
        this.props.data.editingMode !== undefined ? this.props.onUpdate('editingMode', mode) : this.props.onAdd('editingMode', mode);
    };

    handleEditingOptionChange = (option) => {
        this.props.data.editingOption && this.props.data.editingOption.includes(option) ?
            this.props.onDelete('editingOption', option)
            :
            this.props.onAdd('editingOption', option);
    };

    handleHiddenPropertyChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        checked ?
            this.props.onAdd('hiddenProp', property)
            :
            this.props.onDelete('hiddenProp', property);
    };

    handleOptionChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        property in this.props.data ?
            this.props.onUpdate(property, checked)
            :
            this.props.onAdd(property, checked);
    };

    handleClashChange = (clashType, event) => {
        const property = event.target.name;
        property in this.props.data && this.props.data[property].includes(clashType) ?
            this.props.onDelete(property, clashType)
            :
            this.props.onAdd(property, clashType);
    };

    handleExcludeDeprecated = (status) => {
        this.props.data.excludeDeprecated !== undefined ?
            this.props.onUpdate('excludeDeprecated', status)
            :
            this.props.onAdd('excludeDeprecated', true);
    };

    handleIriSettingChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        checked ?
            this.props.onAdd(property, "-")
            :
            this.props.onDelete(property, "-");
    };

    handleLinkedDataChange = (event) => {
        const checked = event.target.checked;
        checked ?
            this.props.onAdd('linkedData', event.target.name)
            :
            this.props.onDelete('linkedData', event.target.name);
    };

    doRunBatchOperation(action) {
        this.cancellable = makeCancelable(GS.operate(this.props.batchupdateName, this.props.graph, {
            action,
            component: this.props.id
        }));
        return this.cancellable;
    }

    runBatchOperation = async (action) => {
        try {
            await GS.waitForEditsToClear();
            await this.doRunBatchOperation(action).promise;
        }
        catch (e) {
            this.setState({ isErrored: true, errorMessage: e.message });
        }
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onExitWarning = () => this.setState({ isErrored: false });

    render() {

        const { classes, data, includeNotes, includeSlashHash, includeXL, includeVersionIRI, includeAnnotations, includeAnnotate, canHideProps, entitlements, baseInferredClass, includeNotesAsResources, includeLinkedData, linkedProjectTypes,
            includeTemplates, includeLinkedResources, includeDeprecatedExclusion, resourceTypeTitle, ontologyProjects, editingOptions,
            onToggleView, includeVersionInfo, includeAdditionalMetadata, projectType, findResourceDialog, includeSorting, setToDraft,
            includeEditingMode, includeClasses, entitlement, graph, id, iriOptions, includeWorkflow, associatedProject } = this.props;
        const { workflowUsable, isErrored, errorMessage } = this.state;

        if (isErrored) return <Warning message={errorMessage} dataTest="cd-error" onExitWarning={this.onExitWarning} />;

        const isReadOnly = checkResourceEntitlements(data, entitlement);
        // Note this is taxonomy associations to linked project, not the same as the prop associateProject
        const aps = data.associatedProject ?
            Array.isArray(data.associatedProject) ?
                [...data.associatedProject].sort()
                :
                [data.associatedProject]
            :
            [];
        let resourceClasses = Array.isArray(data.type) ? data.type : [data.type];
        resourceClasses = resourceClasses.map(rc => GS.getIRIfromAlias(rc) || rc);

        let tabs = ["Core"];
        if (includeAdditionalMetadata) tabs.push("Advanced Metadata");

        return (
            <ProjectConsumer>
                {({ id: projectIRI, projectLang, defaultLang, ontologyProjectAssociation, hasComponent }) => (
                    <TabbedSubmenu tabs={tabs} compact={true}>
                        <Grid container spacing={2} classes={
                            {
                                "spacing-xs-2": this.props.classes.fullGrid
                            }
                        }>
                            <Grid item xs={12}>
                                <TitledPanel title="IRI">
                                    <BasicReadField
                                        dataTest="iri"
                                        ariaLabel="Component IRI"
                                        fullWidth
                                        text={data.id} />
                                </TitledPanel>
                            </Grid>
                            {includeVersionIRI &&
                                <Grid item xs={12}>
                                    <TitledPanel title="Version IRI">
                                        <IRI
                                            dataTest="version-iri"
                                            project={projectIRI}
                                            placeholder="No version IRI exists for this ontology."
                                            isReadOnly={isReadOnly}
                                            currentIRI={data.versionIRI}
                                            onDelete={this.onDelete("versionIRI")}
                                            onUpdateIRI={this.onUpdateIRI("versionIRI")}
                                            graph={graph}
                                            baseIRI={id} />
                                    </TitledPanel>
                                </Grid>
                            }
                            <Grid item xs={12}>
                                <BasicLanguageFieldDisplay
                                    dataTest="component-title"
                                    tooltipText="Add title translation"
                                    message="Add Title Translation"
                                    defaultLang={defaultLang}
                                    title="Title"
                                    ariaLabel="Component title"
                                    property="title"
                                    values={data.title}
                                    projectLang={projectLang}
                                    onePerLang={true}
                                    placeholder="Enter title text"
                                    name="Title"
                                    maxLength={MAX_COMPONENT_TITLE_LENGTH}
                                    onDelete={this.onDelete}
                                    onUpdate={this.onUpdate}
                                    isReadOnly={isReadOnly}
                                    onAddLanguage={this.addNewLiteral} />
                            </Grid>
                            <Grid item xs={12}>
                                <BasicLanguageFieldDisplay
                                    dataTest="component-description"
                                    tooltipText="Add description translation"
                                    message="Add Description Translation"
                                    defaultLang={defaultLang}
                                    ariaLabel="Component description"
                                    title="Description"
                                    property="description"
                                    values={data.description}
                                    projectLang={projectLang}
                                    required={false}
                                    onePerLang={true}
                                    placeholder="Enter description text"
                                    name="Description"
                                    maxLength={MAX_COMPONENT_DESC_LENGTH}
                                    onDelete={this.onDelete}
                                    onUpdate={this.onUpdate}
                                    isReadOnly={isReadOnly}
                                    onAddLanguage={this.addNewLiteral} />
                            </Grid>
                            {includeVersionInfo &&
                                <Grid item xs={12}>
                                    <BasicLanguageFieldDisplay
                                        dataTest="version-info"
                                        tooltipText="Provide version information"
                                        message={"Provide Version Information"}
                                        defaultLang={defaultLang}
                                        title="Version Information"
                                        property="versionInfo"
                                        required={false}
                                        values={data.versionInfo}
                                        projectLang={projectLang}
                                        onePerLang={true}
                                        placeholder="Enter version detail"
                                        name="Version Information"
                                        maxLength={MAX_COMPONENT_TITLE_LENGTH}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate}
                                        isReadOnly={isReadOnly}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                            }
                            {includeNotes && noteFields.map(field =>
                                <Grid key={field.title} item xs={12}>
                                    <BasicLanguageFieldDisplay
                                        dataTest={field.property}
                                        tooltipText={"Add " + field.message.toLowerCase()}
                                        message={"Add " + field.message}
                                        defaultLang={defaultLang}
                                        title={field.title}
                                        property={field.property}
                                        values={data[field.property]}
                                        maxLength={MAX_DEFINITION_LENGTH}
                                        projectLang={projectLang}
                                        placeholder={field.placeholder}
                                        name={field.message}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate}
                                        isReadOnly={isReadOnly}
                                        multiline={true}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                            )}
                            {includeWorkflow && workflowUsable &&
                                <Grid item xs={12}>
                                    <TitledPanel title="Workflow">
                                        <LabelledSwitch
                                            dataTest="activate-workflow"
                                            disabled={!entitlements.includes(ENTITLEMENTS.ACTIVATE_WORKFLOW)}
                                            checked={data.activateWorkflow || false}
                                            handleChange={this.handleWorkflowChange}
                                            label="Is Activated" />
                                        {data.activateWorkflow && entitlements.includes(ENTITLEMENTS.BATCH_CHANGE_WORKFLOW_STATUS) &&
                                            <div style={{ backgroundColor: 'white', borderRadius: 4 }}>
                                                <Grid container classes={
                                                    {
                                                        "spacing-xs-2": this.props.classes.fullGrid
                                                    }
                                                } spacing={2}>
                                                    <Grid item xs={9}>
                                                        <TitledPanel title="Set to Draft">
                                                            <span>
                                                                <span>
                                                                    <SecondaryButton dataTest="reset-resources-to-draft" onClick={() => this.runBatchOperation("set" + (setToDraft || resourceTypeTitle) + "ToDraft")}>All {resourceTypeTitle}</SecondaryButton>
                                                                </span>
                                                                {includeXL &&
                                                                    <span style={{ marginLeft: 36 }}>
                                                                        <SecondaryButton dataTest="reset-labels-to-draft" onClick={() => this.runBatchOperation("setLabelsToDraft")}>All XL Labels</SecondaryButton>
                                                                    </span>
                                                                }
                                                                <span style={{ marginLeft: 36 }}>
                                                                    <SecondaryButton dataTest="uncategorised-to-draft" onClick={() => this.runBatchOperation("setUncategorisedToDraft")}>Uncategorised</SecondaryButton>
                                                                </span>
                                                            </span>
                                                        </TitledPanel>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <TitledPanel title="Approve">
                                                            <SecondaryButton dataTest="approve-all" onClick={() => this.runBatchOperation("approveAll")}>All</SecondaryButton>
                                                        </TitledPanel>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        }
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeAnnotate &&
                                <Grid item xs={12} sm={!includeXL && !includeNotesAsResources ?
                                    12
                                    :
                                    includeXL && includeNotesAsResources ?
                                        4
                                        :
                                        6}>
                                    <TitledPanel title="Annotate">
                                        <LabelledSwitch
                                            dataTest="activate-annotate"
                                            disabled={isReadOnly}
                                            checked={data.activateAnnotate || false}
                                            handleChange={this.handleAnnotateChange}
                                            label="Is Activated" />
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeXL &&
                                <Grid item xs={12} sm={!includeAnnotate && !includeNotesAsResources ?
                                    12
                                    :
                                    includeAnnotate && includeNotesAsResources ?
                                        4
                                        :
                                        6}>
                                    <TitledPanel title="Use XL for Labels">
                                        <LabelledSwitch
                                            dataTest="defaultToXL"
                                            disabled={isReadOnly}
                                            checked={data.useXL}
                                            handleChange={this.handleXLChange}
                                            label="Yes" />
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeNotesAsResources &&
                                <Grid item xs={12} sm={!includeAnnotate && !includeXL ?
                                    12
                                    :
                                    includeAnnotate && includeXL ?
                                        4
                                        :
                                        6}>
                                    <TitledPanel title="Use Resources for Notes">
                                        <LabelledSwitch
                                            dataTest="notesForResources"
                                            disabled={isReadOnly}
                                            checked={data.resourcesForNotes}
                                            handleChange={this.handleNotesChange}
                                            label="Yes" />
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeEditingMode &&
                                <Grid item xs={12} sm={12}>
                                    <TitledPanel dataTest="editing-mode" height="full" title="Editing Mode">
                                        <FormControl component="fieldset" style={{ marginLeft: 12, marginTop: 8 }}>
                                            <RadioGroup
                                                row
                                                aria-label="Editing Mode"
                                                name="editingMode"
                                                value={data.editingMode || "Flex"}
                                                onChange={this.handleModeChange}>
                                                <FormControlLabel data-test="select-flex"
                                                    value="Flex"
                                                    disabled={isReadOnly}
                                                    control={<Radio color="primary" />}
                                                    label="Flexible"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                                <FormControlLabel data-test="select-poly"
                                                    value="Poly"
                                                    disabled={isReadOnly}
                                                    control={<Radio color="primary" />}
                                                    label="Polyhierarchy"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                                <FormControlLabel data-test="select-mono"
                                                    value="Mono"
                                                    disabled={isReadOnly}
                                                    control={<Radio color="primary" />}
                                                    label="Monohierarchy"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                                <FormControlLabel data-test="select-flat"
                                                    value="Flat"
                                                    disabled={isReadOnly}
                                                    control={<Radio color="primary" />}
                                                    label="Flat"
                                                    classes={{ label: classes.formLabelInline }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeAnnotations &&
                                <Grid item xs={12}>
                                    <AnnotationPropertyDisplay
                                        graph={graph}
                                        title="Annotation Properties"
                                        isReadOnly={isReadOnly}
                                        components={hasComponent}
                                        defaultLang={defaultLang}
                                        onToggleView={onToggleView}
                                        viewLang={defaultLang}
                                        onAddAnnotation={this.onAddAnnotation}
                                        projectIRI={projectIRI}
                                        id={id}
                                        values={data.availableAnnotationProperty} />
                                </Grid>
                            }
                            {includeTemplates &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="associate-template" height="full" title="Templates to Display" actions={
                                        !isReadOnly ?
                                            <UseTemplateDialog
                                                graph={graph}
                                                onUseTemplate={this.onUseTemplate}
                                                defaultLang={defaultLang}
                                                projects={[projectIRI]}
                                                exclude={data.associatedTemplate} />
                                            :
                                            null
                                    }>
                                        {
                                            data.associatedTemplate && data.associatedTemplate.map(templateItem =>
                                                <SelectResourceLink
                                                    id={templateItem}
                                                    key={templateItem}
                                                    canDelete={!isReadOnly}
                                                    source={id}
                                                    property="associatedTemplate"
                                                    isReadOnly={isReadOnly}
                                                    defaultLang={defaultLang}
                                                    graph={graph} />
                                            )
                                        }
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeClasses &&
                                <Grid item xs={12}>
                                    <TypeDisplay
                                        graph={graph}
                                        title={"Classes for New " + resourceTypeTitle}
                                        classIRIs={data.defaultClass || []}
                                        highlightNotFound={true}
                                        property="defaultClass"
                                        ontologyProjectAssociation={ontologyProjectAssociation}
                                        isReadOnly={isReadOnly}
                                        defaultLang={defaultLang}
                                        viewLang={defaultLang}
                                        onAddClass={this.onAddClass}
                                        id={id}
                                        values={data.defaultClass} />
                                </Grid>
                            }
                            {includeSorting &&
                                <Grid item xs={12}>
                                    <PropertyDisplay
                                        graph={graph}
                                        title={"Use Custom Property for Sorting"}
                                        key={data.sortProperty}
                                        resourceIRI={data.sortProperty}
                                        sortNumerically={data.sortNumerically}
                                        highlightNotFound={true}
                                        ontologyProjects={ontologyProjects}
                                        ontologyProjectAssociation={ontologyProjectAssociation}
                                        isReadOnly={isReadOnly}
                                        source={id}
                                        defaultLang={defaultLang}
                                        viewLang={defaultLang}
                                        handleSortChange={this.handleSortChange}
                                        onAddProperty={this.onAddProperty} />
                                </Grid>
                            }
                            {canHideProps &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="hide-properties" height="full" title="Hide Properties from View">
                                        <div style={{ marginLeft: 12 }}>
                                            <Grid container>
                                                {
                                                    canHideProps.map(propItem =>
                                                        <Grid item xs={4} key={propItem.property}>
                                                            <FormControlLabel
                                                                data-test={"hide-" + propItem.property}
                                                                control={
                                                                    <Checkbox key={propItem.property}
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly}
                                                                        name={propItem.property}
                                                                        className={classes.checked}
                                                                        checked={data.hiddenProp ? data.hiddenProp.includes(propItem.property) : false}
                                                                        onClick={(ev) => this.handleHiddenPropertyChange(ev)} />}
                                                                label={propItem.label}
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                    )
                                                }
                                            </Grid>
                                        </div>
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeLinkedResources &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="linked-projects" height="full" title="Linked Projects" actions={
                                        !isReadOnly ?
                                            <SelectProjectDialog
                                                onAddProject={this.addProject}
                                                button="small"
                                                exclude={[projectIRI, ...aps]}
                                                projectTypes={linkedProjectTypes}
                                                includeIcon={false} />
                                            :
                                            null
                                    }>
                                        {
                                            aps.map(ap =>
                                                <OtherProjectLink
                                                    id={ap}
                                                    key={ap}
                                                    handleNotAvailable={true}
                                                    graph={getGraphFromProject(ap)}
                                                    sourceGraph={graph}
                                                    defaultLang={defaultLang}
                                                    viewLang={defaultLang}
                                                    propertyGraph={getGraphFromProject(ap)}
                                                    source={id}
                                                    isReadOnly={isReadOnly}
                                                    property="associatedProject" />
                                            )
                                        }
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeLinkedData &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="linked-data-resources" height="full" title="Linked Data Resources">
                                        <div style={{ marginLeft: 12 }}>
                                            <Grid container>
                                                {
                                                    linkedDataSources.map(ldItem =>
                                                        <Grid item xs={4} key={ldItem.name}>
                                                            <FormControlLabel
                                                                data-test={ldItem.name}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly}
                                                                        name={ldItem.name}
                                                                        className={classes.checked}
                                                                        checked={data.linkedData ?
                                                                            data.linkedData.includes(ldItem.name) :
                                                                            false
                                                                        }
                                                                        onClick={(ev) => this.handleLinkedDataChange(ev)} />}
                                                                label={ldItem.name}
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                    )
                                                }
                                            </Grid>
                                        </div>
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeDeprecatedExclusion &&
                                <Grid item xs={12}>
                                    <TitledPanel title="Viewing Settings">
                                        <LabelledSwitch
                                            dataTest="exclude-deprecated"
                                            disabled={isReadOnly}
                                            checked={data.excludeDeprecated || false}
                                            handleChange={this.handleExcludeDeprecated}
                                            label="Hide Deprecated Resources (Top Level Only)" />
                                    </TitledPanel>
                                </Grid>
                            }
                            {editingOptions &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="editing-options" title="Editing Options">
                                        <div style={{ marginLeft: 12 }}>
                                            <Grid container>
                                                {editingOptions.map((eo, eoIndex) => {
                                                    return <Grid
                                                        key={eoIndex}
                                                        item xs={4}>
                                                        <FormControlLabel
                                                            data-test={"option-" + eo}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={isReadOnly
                                                                        || (data.editingOption && data.editingOption.includes(eo) && eo === "Advanced View")
                                                                    }
                                                                    name={eo}
                                                                    className={classes.checked}
                                                                    checked={data.editingOption && data.editingOption.includes(eo) ?
                                                                        true
                                                                        :
                                                                        false
                                                                    }
                                                                    onClick={() => this.handleEditingOptionChange(eo, eoIndex)} />}
                                                            label={eo}
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                    </Grid>;
                                                })}
                                            </Grid>
                                        </div>
                                    </TitledPanel>
                                </Grid>
                            }
                            {includeSlashHash &&
                                <React.Fragment>
                                    <Grid item xs={6}>
                                        <TitledPanel dataTest="iri-pattern-option" height="full" title="IRI Pattern">
                                            {data.useSlashIRI ?
                                                <Typography data-test="iri-pattern" variant="body2">Slash</Typography>
                                                :
                                                <Typography data-test="iri-pattern" variant="body2">Hash</Typography>
                                            }
                                        </TitledPanel>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TitledPanel dataTest="iri-format-option" height="full" title="IRI Format">
                                            {data.useUuidIRI ?
                                                <Typography data-test="iri-format" variant="body2">UUID</Typography>
                                                :
                                                data.useCounterIRI ?
                                                    <Typography data-test="iri-format" variant="body2">Counter</Typography>
                                                    :
                                                    <Typography data-test="iri-format" variant="body2">Label</Typography>
                                            }
                                            {data.useCounterIRI &&
                                                <div data-test="iri-counter" style={{ marginTop: 12 }}>
                                                    <Typography data-test="primary-counter" variant="body2">Primary Counter Value: {data.primaryCounter + 1}</Typography>
                                                    {includeXL &&
                                                        <Typography data-test="secondary-counter" variant="body2">Secondary Counter Value: {data.secondaryCounter + 1}</Typography>
                                                    }
                                                </div>
                                            }
                                        </TitledPanel>
                                    </Grid>
                                </React.Fragment>
                            }
                            {iriOptions && !data.useCounterIRI &&
                                <Grid item xs={12}>
                                    <TitledPanel dataTest="iri-options" title="IRI Options">
                                        <div style={{ marginLeft: 12 }}>
                                            <Grid container>
                                                {!data.useUuidIRI && iriOptions.includes("propertiesInitialLowerCase") &&
                                                    <Grid item xs={12}>
                                                        <FormControlLabel
                                                            data-test={"option-propertiesInitialLowerCase"}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={isReadOnly}
                                                                    name={"propertiesInitialLowerCase"}
                                                                    className={classes.checked}
                                                                    checked={data.propertiesInitialLowerCase || false}
                                                                    onClick={(ev) => this.handleOptionChange(ev)} />}
                                                            label="Properties Have Lower Case First Letter"
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                    </Grid>
                                                }
                                                {iriOptions.includes("showIRI") &&
                                                    <Grid item xs={12}>
                                                        <FormControlLabel
                                                            data-test={"option-showIRI"}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    color="primary"
                                                                    disabled={isReadOnly}
                                                                    name={"showIRI"}
                                                                    className={classes.checked}
                                                                    checked={data.showIRI || false}
                                                                    onClick={(ev) => this.handleOptionChange(ev)} />}
                                                            label="Always Display IRIs When Adding Resources"
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                    </Grid>
                                                }
                                                {!data.useUuidIRI && iriOptions &&
                                                    <React.Fragment>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                data-test={"option-removeSpaces"}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly}
                                                                        name={"iriRemoveSpaces"}
                                                                        className={classes.checked}
                                                                        checked={data.iriRemoveSpaces || false}
                                                                        onClick={(ev) => this.handleOptionChange(ev)} />}
                                                                label="Remove Spaces"
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                data-test={"option-replaceSpaces"}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly || data.iriRemoveSpaces}
                                                                        name={"iriReplaceSpaces"}
                                                                        className={classes.checked}
                                                                        checked={data.iriReplaceSpaces === "-" || false}
                                                                        onClick={(ev) => this.handleIriSettingChange(ev)} />}
                                                                label="Replace Spaces (With Hyphens)"
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                data-test={"option-removeSpecialCharacters"}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly}
                                                                        name={"iriRemoveSpecialCharacters"}
                                                                        className={classes.checked}
                                                                        checked={data.iriRemoveSpecialCharacters || false}
                                                                        onClick={(ev) => this.handleOptionChange(ev)} />}
                                                                label="Remove Special Characters"
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                data-test={"option-replaceSpecialCharacters"}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly || data.iriRemoveSpecialCharacters}
                                                                        name={"iriReplaceSpecialCharacters"}
                                                                        className={classes.checked}
                                                                        checked={data.iriReplaceSpecialCharacters === "-" || false}
                                                                        onClick={(ev) => this.handleIriSettingChange(ev)} />}
                                                                label="Replace Special Characters (With Hyphens)"
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                data-test={"option-minimiseEncoding"}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        color="primary"
                                                                        disabled={isReadOnly}
                                                                        name={"minimiseEncoding"}
                                                                        className={classes.checked}
                                                                        checked={data.minimiseEncoding || false}
                                                                        onClick={(ev) => this.handleOptionChange(ev)} />}
                                                                label="Minimise IRI Encoding"
                                                                classes={{ label: classes.formLabelInline }}
                                                            />
                                                        </Grid>
                                                        {includeXL &&
                                                            <React.Fragment>
                                                                <Grid item xs={12}>
                                                                    <div style={{ marginTop: 12 }}>
                                                                        <Typography variant="caption">For Labels Only</Typography>
                                                                    </div>
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <FormControlLabel
                                                                        data-test={"option-prependLabel"}
                                                                        control={
                                                                            <Checkbox
                                                                                size="small"
                                                                                color="primary"
                                                                                disabled={isReadOnly}
                                                                                name={"iriPrependLabel"}
                                                                                className={classes.checked}
                                                                                checked={"iriPrependLabel" in data ? data.iriPrependLabel : true}
                                                                                onClick={(ev) => this.handleOptionChange(ev)} />}
                                                                        label="Prepend Label IRI"
                                                                        classes={{ label: classes.formLabelInline }}
                                                                    />
                                                                    {data.iriPrependLabel !== false &&
                                                                        <div style={{ marginLeft: 24, marginTop: -12, maxWidth: 200 }}>
                                                                            <LiteralConfirmationField
                                                                                displayDatatype={false}
                                                                                margin="dense"
                                                                                ariaLabel="Prepend label IRI"
                                                                                maxLength={10}
                                                                                allowSpaces={false}
                                                                                dataTest="prepend-label"
                                                                                text={data.iriPrependLabelText || "label"}
                                                                                datatype="http://www.w3.org/2001/XMLSchema#string"
                                                                                canBeDeleted={false}
                                                                                onUpdate={this.onUpdatePrependLabel}
                                                                                isReadOnly={isReadOnly} />
                                                                        </div>
                                                                    }
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <FormControlLabel
                                                                        data-test={"option-appendLanguageWithLanguageCode"}
                                                                        control={
                                                                            <Checkbox
                                                                                size="small"
                                                                                color="primary"
                                                                                disabled={isReadOnly}
                                                                                name={"iriAppendLabelWithLanguageCode"}
                                                                                className={classes.checked}
                                                                                checked={"iriAppendLabelWithLanguageCode" in data ? data.iriAppendLabelWithLanguageCode : true}
                                                                                onClick={(ev) => this.handleOptionChange(ev)} />}
                                                                        label="Append Language Code to Label IRI"
                                                                        classes={{ label: classes.formLabelInline }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <FormControlLabel
                                                                        data-test={"option-handleLabelIRIclash"}
                                                                        control={
                                                                            <Checkbox
                                                                                size="small"
                                                                                color="primary"
                                                                                disabled={isReadOnly}
                                                                                name={"iriHandleIRIclash"}
                                                                                className={classes.checked}
                                                                                checked={"iriHandleIRIclash" in data &&
                                                                                    data.iriHandleIRIclash.includes("http://www.w3.org/2008/05/skos-xl#Label")}
                                                                                onClick={(ev) => this.handleClashChange("http://www.w3.org/2008/05/skos-xl#Label", ev)} />}
                                                                        label="Automatically Handle IRI Clashes"
                                                                        classes={{ label: classes.formLabelInline }}
                                                                    />
                                                                </Grid>
                                                            </React.Fragment>
                                                        }
                                                    </React.Fragment>
                                                }
                                            </Grid>
                                        </div>
                                    </TitledPanel>
                                </Grid>
                            }
                        </Grid>
                        {includeAdditionalMetadata &&
                            <div style={{ margin: '0px 6px 12px 6px' }}>
                                <Typography variant="body2">Additional metadata that is configured using property templates.</Typography>
                                <Grid container spacing={1}>
                                    {ontologyProjectAssociation &&
                                        [...ontologyProjectAssociation].sort().map((opa) =>
                                            <AssociatedProjectDisplay
                                                resourceData={data}
                                                graph={graph}
                                                dialogComponent={findResourceDialog}
                                                forceBaseClass={true}
                                                baseInferredClass={baseInferredClass}
                                                projectIRI={projectIRI}
                                                projectType={projectType}
                                                schemeIRI={id}
                                                useWorkflow={data.activateWorkflow}
                                                propertyTemplates={data.associatedTemplate}
                                                isReadOnly={isReadOnly}
                                                viewLang={defaultLang}
                                                projectLang={projectLang}
                                                linkedData={data.linkedData}
                                                associatedTaxonomyProject={associatedProject}
                                                addNewLiteral={this.addNewLiteral}
                                                addNewTypedLiteral={this.addNewTypedLiteral}
                                                includeExistingResource={this.includeExistingResource}
                                                components={hasComponent}
                                                resourceClasses={resourceClasses}
                                                onDelete={this.onDelete}
                                                onUpdate={this.onActualUpdate}
                                                key={opa}
                                                id={opa}
                                                defaultLang={defaultLang} />
                                        )
                                    }
                                </Grid>
                            </div>
                        }
                    </TabbedSubmenu>
                )
                }
            </ProjectConsumer>
        );
    }
}

ComponentDetail.defaultProps = {
    resourceTypeTitle: "Concepts",
    includeSlashHash: false,
    includeNotes: false,
    includeClasses: false,
    includeEditingMode: false,
    includeTemplates: false,
    includeVersionIRI: false,
    includeLinkedResources: false,
    includeAnnotations: false,
    includeXL: false,
    includeVersionInfo: false,
    includeWorkflow: false,
    includeDeprecatedExclusion: false,
    includeLinkedData: false,
    includeSorting: false,
    includeAdditionalMetadata: false,
    linkedProjectTypes: [
        "https://grafsync.graphifi.com/schema/gs/TaxonomyProject"
    ]
};

ComponentDetail.propTypes = {
    includeSlashHash: PropTypes.bool,
    includeNotes: PropTypes.bool,
    textRef: PropTypes.string,
    batchupdateName: PropTypes.string,
    canHideProps: PropTypes.array,
    includeTemplates: PropTypes.bool,
    includeAdditionalMetadata: PropTypes.bool,
    includeEditingMode: PropTypes.bool,
    baseInferredClass: PropTypes.string,
    includeLinkedResources: PropTypes.bool,
    includeLinkedData: PropTypes.bool,
    includeAnnotations: PropTypes.bool,
    includeVersionIRI: PropTypes.bool,
    includeAnnotate: PropTypes.bool,
    includeSorting: PropTypes.bool,
    includeWorkflow: PropTypes.bool,
    includeDeprecatedExclusion: PropTypes.bool,
    includeNotesAsResources: PropTypes.bool,
    options: PropTypes.array,
    includeXL: PropTypes.bool,
    includeClasses: PropTypes.bool,
    data: PropTypes.object.isRequired,
    associatedProject: PropTypes.array,
    findResourceDialog: PropTypes.func,
    linkedProjectTypes: PropTypes.array,
    setToDraft: PropTypes.string,
    ontologyProjects: PropTypes.array,
    projectType: PropTypes.string.isRequired,
    iriOptions: PropTypes.array,
    editingOptions: PropTypes.array,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    onToggleView: PropTypes.func,
    onAdd: PropTypes.func,
    onUpdate: PropTypes.func,
    onDelete: PropTypes.func,
    entitlement: PropTypes.string.isRequired,
    entitlements: PropTypes.array,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(ComponentDetail));